"use client";

import { ChevronLeft, ChevronRight } from "lucide-react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { useRef, useState } from "react";
import BrowserMockup from "@/components/site/browser-mockup";

type Screenshot = {
	title: string;
	imageUrl: string;
};

export default function BrowserScreenshotCarousel({
	screenshots,
}: {
	screenshots: Screenshot[];
}) {
	const [activeIndex, setActiveIndex] = useState(0);
	const [direction, setDirection] = useState(1);
	const pointerStartX = useRef<number | null>(null);
	const reduceMotion = useReducedMotion();
	const hasMultiple = screenshots.length > 1;

	if (screenshots.length === 0) return null;

	const goTo = (index: number, requestedDirection?: -1 | 1) => {
		const nextActiveIndex =
			(index + screenshots.length) % screenshots.length;

		if (nextActiveIndex === activeIndex) return;

		const forwardDistance =
			(nextActiveIndex - activeIndex + screenshots.length) % screenshots.length;
		const backwardDistance =
			(activeIndex - nextActiveIndex + screenshots.length) % screenshots.length;

		setDirection(
			requestedDirection ?? (forwardDistance <= backwardDistance ? 1 : -1),
		);
		setActiveIndex(nextActiveIndex);
	};

	const previousIndex =
		(activeIndex - 1 + screenshots.length) % screenshots.length;
	const nextIndex = (activeIndex + 1) % screenshots.length;

	const finishSwipe = (clientX: number) => {
		if (pointerStartX.current === null) return;

		const distance = clientX - pointerStartX.current;
		pointerStartX.current = null;

		if (Math.abs(distance) < 48) return;
		goTo(
			distance > 0 ? activeIndex - 1 : activeIndex + 1,
			distance > 0 ? -1 : 1,
		);
	};

	return (
		<div
			role="region"
			aria-roledescription="carousel"
			aria-label="Project screenshots"
			tabIndex={0}
			onKeyDown={(event) => {
				if (!hasMultiple) return;
				if (event.key === "ArrowLeft") {
					event.preventDefault();
					goTo(activeIndex - 1, -1);
				}
				if (event.key === "ArrowRight") {
					event.preventDefault();
					goTo(activeIndex + 1, 1);
				}
			}}
			className="relative mx-auto touch-pan-y py-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-zinc-300 focus-visible:ring-offset-4 focus-visible:ring-offset-[#0b0d12] sm:py-8"
			onPointerDown={(event) => {
				pointerStartX.current = event.clientX;
			}}
			onPointerUp={(event) => finishSwipe(event.clientX)}
			onPointerCancel={() => {
				pointerStartX.current = null;
			}}
		>
			<div className="relative isolate">
				{hasMultiple ? (
					<>
						<button
							type="button"
							onClick={() => goTo(previousIndex, -1)}
							aria-label={`View previous screenshot: ${screenshots[previousIndex].title}`}
							className="absolute left-0 top-1/2 z-0 hidden w-[70%] -translate-x-[58%] -translate-y-1/2 scale-[0.88] cursor-pointer overflow-hidden rounded-xl opacity-40 grayscale-[35%] transition duration-300 ease-out hover:opacity-55 motion-reduce:transition-none sm:block"
						>
							<BrowserMockup screenshot={screenshots[previousIndex]} />
							<span className="pointer-events-none absolute inset-0 bg-[#050706]/45" />
						</button>

						<button
							type="button"
							onClick={() => goTo(nextIndex, 1)}
							aria-label={`View next screenshot: ${screenshots[nextIndex].title}`}
							className="absolute right-0 top-1/2 z-0 hidden w-[70%] translate-x-[58%] -translate-y-1/2 scale-[0.88] cursor-pointer overflow-hidden rounded-xl opacity-40 grayscale-[35%] transition duration-300 ease-out hover:opacity-55 motion-reduce:transition-none sm:block"
						>
							<BrowserMockup screenshot={screenshots[nextIndex]} />
							<span className="pointer-events-none absolute inset-0 bg-[#050706]/45" />
						</button>
					</>
				) : null}

				<AnimatePresence initial={false} custom={direction} mode="popLayout">
					<motion.div
						key={`${screenshots[activeIndex].imageUrl}-${activeIndex}`}
						custom={direction}
						variants={{
							enter: (slideDirection: number) => ({
								x: reduceMotion
									? 0
									: slideDirection > 0
										? "7%"
										: "-7%",
								opacity: reduceMotion ? 1 : 0,
								scale: reduceMotion ? 1 : 0.985,
							}),
							center: { x: 0, opacity: 1, scale: 1 },
							exit: (slideDirection: number) => ({
								x: reduceMotion
									? 0
									: slideDirection > 0
										? "-7%"
										: "7%",
								opacity: reduceMotion ? 1 : 0,
								scale: reduceMotion ? 1 : 0.985,
							}),
						}}
						initial="enter"
						animate="center"
						exit="exit"
						transition={{
							duration: reduceMotion ? 0 : 0.48,
							ease: [0.22, 1, 0.36, 1],
						}}
						aria-live="polite"
						aria-label={`Screenshot ${activeIndex + 1} of ${screenshots.length}`}
						className="relative z-10 mx-auto w-full sm:w-[78%]"
					>
						<BrowserMockup screenshot={screenshots[activeIndex]} priority />
					</motion.div>
				</AnimatePresence>

				{hasMultiple ? (
					<>
						<button
							type="button"
							onClick={() => goTo(activeIndex - 1, -1)}
							aria-label="Previous screenshot"
							className="absolute left-2 top-1/2 z-20 flex h-10 w-10 -translate-y-1/2 cursor-pointer items-center justify-center rounded-full border border-white/20 bg-black/60 text-white/70 shadow-[0_14px_34px_rgba(0,0,0,0.42),inset_0_0_18px_rgba(255,255,255,0.04)] backdrop-blur transition-colors duration-200 hover:border-white/40 hover:bg-white/[0.06] hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-zinc-300 motion-reduce:transition-none sm:left-0 sm:h-11 sm:w-11 lg:-left-1"
						>
							<ChevronLeft aria-hidden="true" className="h-5 w-5 sm:h-6 sm:w-6" strokeWidth={1.5} />
						</button>
						<button
							type="button"
							onClick={() => goTo(activeIndex + 1, 1)}
							aria-label="Next screenshot"
							className="absolute right-2 top-1/2 z-20 flex h-10 w-10 -translate-y-1/2 cursor-pointer items-center justify-center rounded-full border border-white/20 bg-black/60 text-white/70 shadow-[0_14px_34px_rgba(0,0,0,0.42),inset_0_0_18px_rgba(255,255,255,0.04)] backdrop-blur transition-colors duration-200 hover:border-white/40 hover:bg-white/[0.06] hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-zinc-300 motion-reduce:transition-none sm:right-0 sm:h-11 sm:w-11 lg:-right-1"
						>
							<ChevronRight aria-hidden="true" className="h-5 w-5 sm:h-6 sm:w-6" strokeWidth={1.5} />
						</button>
					</>
				) : null}
			</div>

			{hasMultiple ? (
				<div className="mt-5 flex flex-wrap items-center justify-center gap-0.5 sm:mt-9 sm:gap-1">
					{screenshots.map((screenshot, index) => {
						const isActive = index === activeIndex;

						return (
							<button
								key={`${screenshot.imageUrl}-${index}`}
								type="button"
								onClick={() => goTo(index)}
								aria-label={`Go to screenshot ${index + 1}: ${screenshot.title}`}
								aria-current={isActive ? "true" : undefined}
								className="group flex h-7 w-7 cursor-pointer items-center justify-center rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-zinc-300"
							>
								<motion.span
									initial={false}
									animate={{
										width: isActive ? 24 : 8,
										backgroundColor: isActive
											? "rgba(244,244,245,1)"
											: "rgba(255,255,255,0.2)",
										boxShadow: isActive
											? "0 0 10px rgba(255,255,255,0.12)"
											: "0 0 0 rgba(255,255,255,0)",
									}}
									transition={
										reduceMotion
											? { duration: 0 }
											: {
													type: "spring",
													stiffness: 380,
													damping: 30,
													mass: 0.7,
												}
									}
									className="block h-2 rounded-full transition-[filter] duration-200 group-hover:brightness-150"
								/>
							</button>
						);
					})}
				</div>
			) : null}
		</div>
	);
}
